import React, { Fragment, useEffect, useState } from "react";
import axios from "axios";
import config from "../../../config";

const SubjectAssignment = () => {
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selected, setSelected] = useState([]); 
  const [lockedMap, setLockedMap] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTeachers();
    fetchSubjects();
    fetchLocks();
  }, []);

  // ===============================
  // LOAD TEACHERS
  // ===============================
  const fetchTeachers = async () => {
    const res = await axios.get(
      `${config.apiBaseUrl}/staffs/teacher-list.php`
    );
    setTeachers(res.data.data || []);
  };

  // ===============================
  // LOAD SUBJECTS (must include subject_id + class_id)
  // ===============================
  const fetchSubjects = async () => {
    try {
      const res = await axios.get(
        `${config.apiBaseUrl}/academic/subjects-list.php`
      );

      const data = res.data.data || [];
      setSubjects(data);
    } catch {
      setSubjects([]);
    }
  };

  // ===============================
  // LOAD GLOBAL LOCKS
  // ===============================
  const fetchLocks = async () => {
    const res = await axios.get(
      `${config.apiBaseUrl}/staffs/all-assignments.php`
    );

    const map = {};
    (res.data.data || []).forEach(a => {
      map[`${a.subject_id}_${a.class_id}`] = a.staff_id;
    });

    setLockedMap(map);
  };

  // ===============================
  // LOAD ASSIGNMENTS FOR SELECTED TEACHER
  // ===============================
  const loadAssignments = async (teacherId) => {
    try {
      const res = await axios.get(
        `${config.apiBaseUrl}/staffs/get-teacher-subjects.php?staff_id=${teacherId}`
      );

      const data = res.data.data || [];

      const formatted = data.map(s => ({
        subject_id: s.subject_id,
        class_id: s.class_id
      }));

      setSelected(formatted);

    } catch {
      setSelected([]);
    }
  };

  const handleTeacherClick = (teacher) => {
    setSelectedTeacher(teacher);
    loadAssignments(teacher.id);
  };

  // ===============================
  // TOGGLE SUBJECT PER CLASS
  // ===============================
  const toggleSubject = (subject) => {
    const exists = selected.find(
      s =>
        s.subject_id === subject.subject_id &&
        s.class_id === subject.class_id
    );

    if (exists) {
      setSelected(prev =>
        prev.filter(
          s =>
            !(
              s.subject_id === subject.subject_id &&
              s.class_id === subject.class_id
            )
        )
      );
    } else {
      setSelected(prev => [
        ...prev,
        {
          subject_id: subject.subject_id,
          class_id: subject.class_id
        }
      ]);
    }
  };

  // ===============================
  // SAVE ASSIGNMENTS
  // ===============================
  const saveAssignments = async () => {
  if (!selectedTeacher) return;

  try {
    setLoading(true);

    const payload = {
      staff_id: selectedTeacher.id,
      assignments: selected
    };

    console.log("Sending payload:", payload);

    await axios.post(
      `${config.apiBaseUrl}/staffs/bulk-assign.php`,
      payload
    );

    await fetchLocks();
    alert("Assignments saved!");

  } catch (err) {
    console.error("Save failed:", err.response?.data || err);
  } finally {
    setLoading(false);
  }
};

  // ===============================
  // GROUP SUBJECTS BY CLASS
  // ===============================
  const grouped = subjects.reduce((acc, sub) => {
    const key = `Class ${sub.grade_name} - ${sub.section_name} (${sub.academic_year})`;

    if (!acc[key]) acc[key] = [];
    acc[key].push(sub);

    return acc;
  }, {});

  return (
    <Fragment>
      <div className="row">
        <div className="col-md-4 border-end">
          <h5>Teachers</h5>
          {teachers.map(t => (
            <div
              key={t.id}
              onClick={() => handleTeacherClick(t)}
              className={`p-2 mb-1 ${
                selectedTeacher?.id === t.id
                  ? "bg-primary text-white"
                  : "bg-light"
              }`}
              style={{ cursor: "pointer" }}
            >
              {t.fullname}
            </div>
          ))}
        </div>

        <div className="col-md-4 border-end">
          <h5>
            {selectedTeacher
              ? "Assign Subjects"
              : "Select a teacher"}
          </h5>

          {selectedTeacher &&
            Object.entries(grouped).map(([label, list]) => (
              <div key={label} className="mb-3">
                <strong>{label}</strong>

                {list.map(sub => {

                  const lockKey = `${sub.subject_id}_${sub.class_id}`;
                  const lockedBy = lockedMap[lockKey];
                  const lockedForOther =
                    lockedBy && lockedBy !== selectedTeacher.id;

                  const checked = selected.some(
                    s =>
                      s.subject_id === sub.subject_id &&
                      s.class_id === sub.class_id
                  );

                  return (
                    <div key={lockKey}>
                      <label
                        className={
                          lockedForOther
                            ? "text-danger fw-bold"
                            : ""
                        }
                      >
                        <input
                          type="checkbox"
                          disabled={lockedForOther}
                          checked={checked}
                          onChange={() => toggleSubject(sub)}
                          className="me-2"
                        />
                        {sub.subject_code} — {sub.subject_name}
                      </label>
                    </div>
                  );
                })}
              </div>
            ))}

          {selectedTeacher && (
            <button
              className="btn btn-primary mt-3"
              onClick={saveAssignments}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Assignments"}
            </button>
          )}
        </div>

        <div className="col-md-4">
          <h5>Assigned Subjects</h5>

          {selected.length === 0 && (
            <div className="text-muted">
              No subjects assigned
            </div>
          )}

          {subjects
            .filter(sub =>
              selected.some(
                s =>
                  s.subject_id === sub.subject_id &&
                  s.class_id === sub.class_id
              )
            )
            .map(sub => (
              <div
                key={`${sub.subject_id}_${sub.class_id}`}
                className="p-1 mb-1 bg-light rounded"
              >
                {sub.subject_code} — {sub.subject_name}
              </div>
            ))}
        </div>
      </div>
    </Fragment>
  );
};

export default SubjectAssignment;